import { Box, FormLabel, Link, Stack, SvgIcon, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import MailIcon from '../../lib/icons/svg/mail.svg?react';
import { Input } from '../../lib/components';
import { Button } from '../../lib/components/atomic/button';
import { useStore } from '../../hooks/useStore';
import { link } from '../../misc/globals';
import { useSnackbar } from 'notistack';
import { Header } from './partials/Header';
import { LoginLogo } from './partials/LoginLogo';

const MAX_WIDTH = '400px';
const MAIL_ICON = <SvgIcon component={MailIcon} />;

export const ForgotPassword: FC = () => {
  const { t } = useTranslation();
  const { authenticationStore } = useStore();
  const snackbar = useSnackbar();
  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email(t('form:INVALID_EMAIL')).required(t('form:FIELD_IS_REQUIRED'))
    }),
    onSubmit: async (values) => {
      try {
        await authenticationStore.sendResetPasswordMail(values.email).then(() => {
          snackbar.enqueueSnackbar({
            message: t('account:PASSWORDS_RESET_MAIL_SENT_TEXT'),
            details: t('account:PASSWORDS_RESET_MAIL_SENT_INFO'),
            variant: 'detailedSuccess'
          });

          formik.resetForm();
        });
      } catch (error) {
        console.error(error);
      }
    }
  });

  const hasEmailError = formik.touched.email && formik.errors.email !== '';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Header />
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '0 32px 64px 32px',
          margin: '20px auto',
          maxWidth: '500px',
          width: '100%'
        }}
      >
        <LoginLogo />
        <Stack
          component="form"
          spacing={2}
          sx={{
            maxWidth: MAX_WIDTH,
            margin: '0 auto',
            '& .MuiInput-root': {
              borderRadius: '16px'
            }
          }}
          onSubmit={formik.handleSubmit}
        >
          <Typography paragraph className="text-primary-main text-left">
            {t('account:FORGOT_PASSWORD_INFO')}
          </Typography>
          <Input
            type="email"
            name="email"
            id="email"
            autoFocus
            placeholder={t('auth:email')}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            startAdornment={MAIL_ICON}
            error={hasEmailError}
            className="gap-3 w-full rounded-2xl"
          />
          {hasEmailError && (
            <FormLabel className="!mt-2" error>
              {formik.errors.email}
            </FormLabel>
          )}
          <Box
            sx={{
              color: 'heaven.main',
              display: 'flex',
              gridTemplateColumns: '1fr 1fr',
              gridColumnGap: '1rem',
              marginTop: hasEmailError ? '1rem!important' : '2rem!important'
            }}
          >
            <Button variant="primary" className="w-full" type="submit">
              {t('common:SEND')}
            </Button>
          </Box>
          <Link href={link.support} target="blank" className="no-underline">
            <Typography variant="body1" color="primary" className="text-center no-underline !mt-8">
              {t('account:NEED_HELP_QUESTION')}
            </Typography>
          </Link>
        </Stack>
      </Box>
    </Box>
  );
};
